// ARQUIVO 100% COMPLETO E CORRIGIDO
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useData from '../hooks/useData';
import useToast from '../hooks/useToast';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import InputField from '../components/ui/InputField';
import CustomSelect from '../components/ui/Select';
import { SettingsManager, ServiceManagerModal, TermsManagerModal, SenderProfileManagerModal, SaveThemeModal } from '../components/modals';
import { SaveIcon, AddIcon, SettingsIcon, DownloadIcon, ToolIcon, EditIcon, ChevronDownIcon, ChevronLeftIcon, DeleteIcon } from '../components/ui/Icons';
import { jsPDF } from "jspdf";
import html2canvas from 'html2canvas';
import useDraggableResizable from '../hooks/useDraggableResizable';
import DropdownMenu from '../components/ui/DropdownMenu';
import DesignToolbar from '../components/quote-generator/DesignToolbar';
import DraggableQuoteItem from '../components/quote-generator/DraggableQuoteItem';
import { getQuoteStatusPillClass, getQuoteStatusColorClass } from '../utils/helpers';
import useClickOutside from '../hooks/useClickOutside';

const QuoteEditorView = ({ initialQuote, onBack }) => {
    const {
        clients, termsTemplates, currentUserProfile, senderProfiles, logo,
        handleSaveQuote,
        activeDesignSettings, handleSaveDesignTheme,
        companyLogoLayout, handleSaveLogoLayout
    } = useData();
    const { showToast } = useToast();

    const getNewQuoteObject = () => ({ id: null, quoteNumber: 'ORC-NOVO', clientId: '', clientName: '', projectName: '', quoteDate: new Date().toISOString().slice(0, 10), items: [], discount: 0, tax: 0, termsContent: "O pagamento deve ser efetuado 50% no início do projeto e 50% na entrega final...", senderProfileId: null, status: 'Rascunho' });

    const [currentQuote, setCurrentQuote] = useState(getNewQuoteObject());
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isServiceModalOpen, setServiceModalOpen] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isSenderProfileModalOpen, setIsSenderProfileModalOpen] = useState(false);
    const [isSaveThemeModalOpen, setSaveThemeModalOpen] = useState(false);
    const quotePreviewRef = useRef(null);
    const [isDesignEditing, setIsDesignEditing] = useState(false);
    const designSettings = activeDesignSettings;
    const [isValuesOpen, setIsValuesOpen] = useState(false);
    
    useEffect(() => {
        setCurrentQuote(initialQuote ? { ...getNewQuoteObject(), ...initialQuote } : getNewQuoteObject());
    }, [initialQuote]);

    const defaultLogoPos = useMemo(() => ({ x: 420, y: 88 }), []);
    const defaultLogoSize = useMemo(() => ({ width: 150, height: 'auto' }), []);
    const initialPos = companyLogoLayout?.position || defaultLogoPos;
    const initialSize = companyLogoLayout?.size || defaultLogoSize;

    const { elementRef: logoRef, position, size } = useDraggableResizable(
        initialPos, initialSize, quotePreviewRef, { lockAspectRatio: true }
    );
    
    const prevIsDesignEditing = useRef(isDesignEditing);
    useEffect(() => {
        if (prevIsDesignEditing.current && !isDesignEditing) {
            handleSaveLogoLayout({ position, size });
            showToast("Posição da logo salva!");
        }
        prevIsDesignEditing.current = isDesignEditing;
    }, [isDesignEditing, position, size, handleSaveLogoLayout, showToast]);
    
    const hourlyRate = useMemo(() => { if (!currentUserProfile || !currentUserProfile.workHoursPerMonth) return 0; const { monthlySalary = 4000, workHoursPerMonth = 160, costFactor = 1.5 } = currentUserProfile; return (monthlySalary * costFactor) / workHoursPerMonth; }, [currentUserProfile]);
    const activeProfile = useMemo(() => senderProfiles.find(p => p.id === currentQuote.senderProfileId), [senderProfiles, currentQuote.senderProfileId]);
    const senderProfileOptions = useMemo(() => senderProfiles.map(p => ({ value: p.id, label: p.name })), [senderProfiles]);
    const clientOptions = useMemo(() => clients.map(c => ({ value: c.id, label: c.name })), [clients]);
    const termsOptions = useMemo(() => termsTemplates.map(t => ({ value: t.id, label: t.title })), [termsTemplates]);
    
    const handleDetailsChange = (e) => { const { name, value } = e.target; setCurrentQuote(prev => ({ ...prev, [name]: value })); };
    const handleItemChange = (index, field, value) => { const newItems = [...(currentQuote.items || [])]; newItems.splice(index, 1, { ...newItems[index], [field]: value }); setCurrentQuote(prev => ({ ...prev, items: newItems })); };
    const addItem = (service = null) => { const newItem = service ? { id: Date.now(), description: service.description, hours: service.hours || 0, qty: 1, subItems: [] } : { id: Date.now(), description: '', hours: 0, qty: 1, subItems: [] }; setCurrentQuote(prev => ({ ...prev, items: [...(prev.items || []), newItem] })); };
    const removeItem = (index) => setCurrentQuote(prev => ({ ...prev, items: (prev.items || []).filter((_, i) => i !== index) }));
    const moveItem = useCallback((dragIndex, hoverIndex) => { setCurrentQuote(prev => { const newItems = [...(prev.items || [])]; const [draggedItem] = newItems.splice(dragIndex, 1); newItems.splice(hoverIndex, 0, draggedItem); return { ...prev, items: newItems }; }); }, []);
    
    const handleSaveCurrentQuote = async () => { if (!currentQuote.clientId || !currentQuote.projectName) { showToast("Selecione um cliente e dê um nome ao projeto.", "error"); return; } const savedId = await handleSaveQuote(currentQuote); if (savedId) { showToast("Orçamento salvo com sucesso!"); if (!currentQuote.id) { setCurrentQuote(prev => ({...prev, id: savedId})); } } else { showToast("Falha ao salvar o orçamento.", "error"); } };
    
    const generatePDF = async () => {
        setIsGenerating(true);
        const wasEditing = isDesignEditing;
        if (wasEditing) setIsDesignEditing(false);
        await new Promise(resolve => setTimeout(resolve, 100));

        const element = quotePreviewRef.current;
        if (!element) {
            setIsGenerating(false);
            return;
        }

        try {
            const canvas = await html2canvas(element, {
                scale: 3, 
                useCORS: true,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
            });

            const imgData = canvas.toDataURL('image/png');
            
            const pdfWidth = 1080;
            const pdfHeight = 1920;

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [pdfWidth, pdfHeight]
            });

            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            
            const canvasAspectRatio = canvasWidth / canvasHeight;
            const pdfAspectRatio = pdfWidth / pdfHeight;

            let finalImgWidth, finalImgHeight, xOffset, yOffset;

            if (canvasAspectRatio > pdfAspectRatio) {
                finalImgWidth = pdfWidth;
                finalImgHeight = pdfWidth / canvasAspectRatio;
                xOffset = 0;
                yOffset = (pdfHeight - finalImgHeight) / 2;
            } else {
                finalImgHeight = pdfHeight;
                finalImgWidth = pdfHeight * canvasAspectRatio;
                yOffset = 0;
                xOffset = (pdfWidth - finalImgWidth) / 2;
            }

            pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalImgWidth, finalImgHeight);
            pdf.save(`orcamento-${currentQuote.projectName || 'projeto'}.pdf`);

        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            showToast("Ocorreu um erro ao gerar o PDF.", "error");
        } finally {
            if (wasEditing) setIsDesignEditing(true);
            setIsGenerating(false);
        }
    };

    const subtotal = (currentQuote.items || []).reduce((acc, item) => acc + (item.hours || 0) * (item.qty || 1) * hourlyRate, 0);
    const discountAmount = (subtotal * currentQuote.discount) / 100;
    const subtotalAfterDiscount = subtotal - discountAmount;
    const taxAmount = subtotalAfterDiscount * (currentQuote.tax / 100);
    const total = subtotalAfterDiscount + taxAmount;

    return (
        <>
            {isGenerating && <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center text-white text-xl animate-fade-in">Gerando PDF...</div>}
            {isDesignEditing && <DesignToolbar onClose={() => setIsDesignEditing(false)} onSaveThemeRequest={() => setSaveThemeModalOpen(true)} />}
            <div className="flex justify-between items-center mb-8">
                <div className='flex items-center gap-2'>
                    <GlassButton onClick={onBack} variant="minimal" className="!p-2.5"><ChevronLeftIcon size={20} /></GlassButton>
                    <h1 className="text-3xl font-bold text-white">{currentQuote.id ? 'Editar Orçamento' : 'Novo Orçamento'}</h1>
                </div>
                <div className="flex items-center space-x-2">
                    <GlassButton onClick={handleSaveCurrentQuote} variant="minimal"><SaveIcon size={16} /><span>Salvar</span></GlassButton>
                    <DropdownMenu trigger={<GlassButton variant="minimal" title="Mais Opções" className="!p-2.5"><SettingsIcon size={20} /></GlassButton>}>
                        <a href="#" onClick={(e) => { e.preventDefault(); setIsDesignEditing(!isDesignEditing); }} className={`flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/10 ${isDesignEditing ? 'text-indigo-400' : 'text-white'}`}><EditIcon size={16} /> {isDesignEditing ? 'Concluir Edição' : 'Editar Design'}</a>
                        <a href="#" onClick={(e) => { e.preventDefault(); setServiceModalOpen(true); }} className="flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-white/10"><ToolIcon size={16} /> Banco de Serviços</a>
                        <a href="#" onClick={(e) => { e.preventDefault(); setIsSettingsModalOpen(true); }} className="flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-white/10"><SettingsIcon size={16} /> Config. de Cálculo</a>
                    </DropdownMenu>
                </div>
            </div>
            {hourlyRate <= 0 && ( <div className="bg-yellow-500/20 border border-yellow-500 text-yellow-300 p-4 rounded-lg mb-6 text-center">Atenção: Seu valor/hora não está configurado. Os preços dos itens serão R$ 0,00.<button onClick={() => setIsSettingsModalOpen(true)} className="font-bold underline ml-2 hover:text-white">Clique aqui para configurar.</button></div> )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-start">
                <GlassCard className={`space-y-6 transition-all duration-300 ${isDesignEditing ? 'opacity-20 pointer-events-none' : 'opacity-100'}`}>
                    <div>
                        <h2 className="text-xl font-bold text-white mb-4">Detalhes do Orçamento</h2>
                        <div className="space-y-4">
                            <div className="p-4 bg-black/20 rounded-lg space-y-4"><div className="flex justify-between items-center"><label className="text-sm font-medium text-gray-300">Suas Informações (Remetente)</label><GlassButton onClick={() => setIsSenderProfileModalOpen(true)} variant="minimal" className="!text-xs !py-1 !px-2"><EditIcon size={14}/> Gerenciar</GlassButton></div><CustomSelect options={senderProfileOptions} value={currentQuote.senderProfileId} onChange={(e) => setCurrentQuote(prev => ({...prev, senderProfileId: e.target.value}))} placeholder="Selecione seu perfil de remetente..."/></div>
                            <CustomSelect label="Cliente" options={clientOptions} value={currentQuote.clientId} onChange={(e) => { const clientName = clients.find(c => c.id === e.target.value)?.name || ''; setCurrentQuote(prev => ({ ...prev, clientId: e.target.value, clientName })); }} placeholder="Selecione um cliente..."/>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputField label="Nome do Projeto" name="projectName" value={currentQuote.projectName} onChange={handleDetailsChange}/>
                                <InputField label="Nº do Orçamento" name="quoteNumber" value={currentQuote.quoteNumber} onChange={handleDetailsChange}/>
                            </div>
                            <InputField label="Data" name="quoteDate" type="date" value={currentQuote.quoteDate} onChange={handleDetailsChange} />
                        </div>
                    </div>
                    <hr className="border-white/10"/>
                    <div>
                        <h3 className="text-lg font-bold text-white mb-3">Itens do Orçamento</h3>
                        <div className="space-y-2">{(currentQuote.items || []).map((item, index) => ( <DraggableQuoteItem key={item.id || index} index={index} item={item} moveItem={moveItem} handleItemChange={handleItemChange} removeItem={removeItem} hourlyRate={hourlyRate} /> ))}</div>
                        <GlassButton onClick={() => addItem()} variant="minimal" className="!px-2 !py-1 mt-2"><AddIcon size={16} /><span>Adicionar Item Manual</span></GlassButton>
                    </div>
                    <hr className="border-white/10"/>
                    <div>
                        <button onClick={() => setIsValuesOpen(!isValuesOpen)} className="w-full flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white">Valores e Termos</h3>
                            <ChevronDownIcon size={20} className={`transition-transform duration-300 ${isValuesOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isValuesOpen ? 'max-h-[1000px] mt-3' : 'max-h-0'}`}>
                            <div className="grid grid-cols-2 gap-4"><InputField label="Desconto (%)" name="discount" type="number" value={currentQuote.discount} onChange={(e) => setCurrentQuote(p => ({...p, discount: parseFloat(e.target.value) || 0}))} /><InputField label="Taxas/Impostos (%)" name="tax" type="number" value={currentQuote.tax} onChange={(e) => setCurrentQuote(p => ({ ...p, tax: parseFloat(e.target.value) || 0 }))} /></div>
                            <div className="mt-4"><div className="flex justify-between items-center mb-1"><label className="block text-sm font-medium text-gray-300">Termos e Condições</label><GlassButton onClick={() => setIsTermsModalOpen(true)} variant="minimal" className="!px-2 !py-1"><EditIcon size={14}/> Gerenciar</GlassButton></div><CustomSelect options={termsOptions} placeholder="Selecionar um template..." value={termsTemplates.find(t => t.content === currentQuote.termsContent)?.id || ''} onChange={(e) => { const content = termsTemplates.find(t => t.id === e.target.value)?.content || currentQuote.termsContent; setCurrentQuote(prev => ({ ...prev, termsContent: content })); }}/><textarea rows="4" className="w-full p-2 bg-black/20 rounded mt-2 text-sm" value={currentQuote.termsContent} onChange={e => setCurrentQuote(p => ({ ...p, termsContent: e.target.value }))} /></div>
                        </div>
                    </div>
                </GlassCard>
                
                <GlassCard className="flex flex-col aspect-ratio-9-16 overflow-y-auto">
                    <div ref={quotePreviewRef} className="pdf-render bg-white p-12 rounded-lg shadow-2xl flex-1 flex flex-col relative" style={{ fontFamily: designSettings.fontFamily, boxShadow: isDesignEditing ? '0 0 0 3px #4f46e5' : '' }}>
                        {logo?.data && ( <div ref={logoRef} style={{ position: 'absolute', left: `${position.x}px`, top: `${position.y}px`, width: `${size.width}px`, height: `${size.height === 'auto' ? 'auto' : `${size.height}px`}`, cursor: isDesignEditing ? 'move' : 'default', border: isDesignEditing ? '2px dashed rgba(79, 70, 229, 0.5)' : 'none', zIndex: 10, }}><div data-action={isDesignEditing ? 'drag' : ''} className="w-full h-full"><img src={logo.data} alt="Logótipo da Empresa" className="w-full h-full object-contain pointer-events-none" /></div>{isDesignEditing && <div data-action="resize" className="resize-handle" />}</div> )}
                        <div className="w-full">
                            <section className="text-left mb-8"><h2 className="font-bold tracking-tight" style={{ fontSize: `${designSettings.titleFontSize}px`, color: designSettings.headerTextColor }}>ORÇAMENTO</h2><div style={{ fontSize: `${designSettings.quoteHeaderFontSize}px`, color: designSettings.bodyTextColor }}><p>Nº: <span className="font-medium text-gray-700">{currentQuote.quoteNumber}</span></p><p>Data: <span className="font-medium text-gray-700">{new Date(currentQuote.quoteDate).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}</span></p></div></section>
                            <section className="flex justify-between gap-8 mb-8" style={{color: designSettings.bodyTextColor}}>
                                <div><h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: designSettings.accentColor }}>DE:</h4><p className="font-semibold text-gray-800">{activeProfile?.companyName || 'Seu Nome / Empresa'}</p></div>
                                <div className="text-right"><h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: designSettings.accentColor }}>PARA:</h4><p className="font-semibold text-gray-800">{currentQuote.clientName || 'Nome do Cliente'}</p></div>
                            </section>
                            <section className="mb-8"><h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: designSettings.accentColor }}>PROJETO:</h4><p className="font-semibold text-gray-800" style={{color: designSettings.bodyTextColor}}>{currentQuote.projectName || 'Nome do Projeto'}</p></section>
                        </div>
                        <table className="w-full text-sm">
                            <thead style={{ color: designSettings.accentColor }}>
                                <tr className="border-b-2" style={{ borderColor: designSettings.accentColor }}>
                                    <th className="text-left font-bold p-2 tracking-wider uppercase">Descrição</th>
                                    <th className="text-center font-bold p-2 w-20 tracking-wider uppercase">Qtd</th>
                                    <th className="text-right font-bold p-2 w-32 tracking-wider uppercase">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(currentQuote.items || []).flatMap((item, index) => {
                                    const itemSubtotal = (item.hours || 0) * (item.qty || 1) * hourlyRate;
                                    const mainRow = (
                                        <tr key={item.id || index} className="border-b" style={{ borderColor: designSettings.tableLineColor }}>
                                            <td className="p-2 align-top font-medium" style={{ color: designSettings.bodyTextColor }}>
                                                {item.description}
                                            </td>
                                            <td className="p-2 text-center align-top" style={{ color: designSettings.bodyTextColor }}>
                                                {item.qty || 1}
                                            </td>
                                            <td className="p-2 text-right align-top font-semibold" style={{ color: designSettings.bodyTextColor }}>
                                                {itemSubtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                            </td>
                                        </tr>
                                    );
                                    const subItemRows = (item.subItems || []).map((subItem, subIndex) => (
                                        <tr key={`${item.id}-sub-${subIndex}`}>
                                            <td className="pt-0 pb-1 px-2 pl-6 align-top text-xs" style={{ color: '#6b7280' , borderBottom: '1px solid ' + designSettings.tableLineColor + '40' }}>
                                                - {subItem.text}
                                            </td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    ));
                                    return [mainRow, ...subItemRows];
                                })}
                            </tbody>
                            <tfoot style={{ color: designSettings.bodyTextColor }}>
                                <tr><td colSpan="1"></td><td className="text-right p-2 font-medium">Subtotal:</td><td className="text-right p-2">{subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td></tr>
                                {currentQuote.discount > 0 && ( <tr><td colSpan="1"></td><td className="text-right p-2 font-medium">Desconto ({currentQuote.discount}%):</td><td className="text-right p-2 text-red-500">-{discountAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td></tr> )}
                                {currentQuote.tax > 0 && ( <tr><td colSpan="1"></td><td className="text-right p-2 font-medium">Taxas ({currentQuote.tax}%):</td><td className="text-right p-2">{taxAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td></tr> )}
                                <tr className="font-bold"><td colSpan="1"></td><td className="text-right p-1.5 rounded-bl-md text-white" style={{ backgroundColor: designSettings.totalColor }}>TOTAL:</td><td className="text-right p-1.5 rounded-br-md text-white" style={{ backgroundColor: designSettings.totalColor }}>{total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td></tr>
                            </tfoot>
                        </table>
                        <footer className="mt-auto pt-6" style={{borderColor: designSettings.tableLineColor}}><div className="text-xs whitespace-pre-wrap" style={{color: designSettings.bodyTextColor}}><h4 className="font-bold mb-2" style={{ color: designSettings.accentColor }}>Termos e Condições</h4><p>{currentQuote.termsContent}</p></div><p className="text-center text-sm mt-8" style={{color: designSettings.bodyTextColor}}>Obrigado pela sua confiança!</p></footer>
                    </div>
                    <div className="mt-6 text-center"><GlassButton onClick={generatePDF} disabled={isGenerating} className="!bg-emerald-500/50 hover:!bg-emerald-500/60"><DownloadIcon /><span>{isGenerating ? 'Gerando...' : 'Baixar PDF'}</span></GlassButton></div>
                </GlassCard>
            </div>
            <Modal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} title="Configurações do Orçamento" maxWidth="max-w-lg"><SettingsManager onClose={() => setIsSettingsModalOpen(false)}/></Modal>
            <Modal isOpen={isServiceModalOpen} onClose={() => setServiceModalOpen(false)} title="Banco de Serviços" footer={<GlassButton onClick={() => setServiceModalOpen(false)}>Fechar</GlassButton>}><ServiceManagerModal onClose={() => setServiceModalOpen(false)} onSelectService={(service) => addItem(service)} /></Modal>
            <Modal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)}><TermsManagerModal onClose={() => setIsTermsModalOpen(false)} /></Modal>
            <Modal isOpen={isSenderProfileModalOpen} onClose={() => setIsSenderProfileModalOpen(false)}><SenderProfileManagerModal onClose={() => setIsSenderProfileModalOpen(false)} /></Modal>
            <SaveThemeModal isOpen={isSaveThemeModalOpen} onClose={() => setSaveThemeModalOpen(false)} onSave={(themeName) => { handleSaveDesignTheme(themeName, designSettings); showToast(`Tema "${themeName}" salvo com sucesso!`); }}/>
        </>
    );
}

const StatusPillDropdown = ({ quote, onStatusChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    useClickOutside(dropdownRef, () => setIsOpen(false));

    const statusOptions = ['Rascunho', 'Enviado', 'Aprovado', 'Recusado'];

    const handleSelect = (status) => {
        onStatusChange(quote, status);
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className={`px-2 py-1 rounded-full text-xs transition-colors ${getQuoteStatusPillClass(quote.status)} hover:ring-2 hover:ring-white/50`}
            >
                {quote.status}
            </button>
            <div className={`absolute top-full right-0 mt-2 w-36 bg-gray-800/90 backdrop-blur-lg border border-white/10 rounded-lg shadow-xl p-1 z-50 animate-fade-in ${isOpen ? 'block' : 'hidden'}`}>
                <div className="space-y-1">
                    {statusOptions.map(option => (
                        <button
                            key={option}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelect(option);
                            }}
                            className="w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-sm text-gray-200 transition-colors hover:bg-white/10 whitespace-nowrap"
                        >
                            <div className={`w-2 h-2 rounded-full ${getQuoteStatusColorClass(option)}`}></div>
                            <span>{option}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const QuotesDashboardView = ({ onNewQuote, onLoadQuote, onDeleteQuote }) => {
    const { quotes, clients, currentUserProfile, handleUpdateQuoteStatus, handleConvertQuoteToProject } = useData();
    const { showToast } = useToast();

    const handleStatusChange = async (quote, status) => {
        await handleUpdateQuoteStatus(quote.id, status);
        showToast("Status do orçamento atualizado!");
        
        if (status === 'Aprovado') {
            if (quote) {
                const success = await handleConvertQuoteToProject(quote);
                if (success) {
                    showToast(`Projeto "${quote.projectName}" criado com sucesso!`, 'success');
                } else {
                    showToast(`Falha ao converter orçamento em projeto.`, 'error');
                }
            }
        }
    };
    
    const handleDeleteClick = (e, quote) => {
        e.stopPropagation();
        onDeleteQuote(quote);
    };

    const calculateTotal = useCallback((items, hourlyRate) => {
        if (!items || hourlyRate <= 0) return 0;
        return items.reduce((acc, item) => acc + (item.hours || 0) * (item.qty || 1) * hourlyRate, 0);
    }, []);

    const hourlyRate = useMemo(() => {
        if (!currentUserProfile || !currentUserProfile.workHoursPerMonth) return 0;
        const { monthlySalary = 4000, workHoursPerMonth = 160, costFactor = 1.5 } = currentUserProfile;
        return (monthlySalary * costFactor) / workHoursPerMonth;
    }, [currentUserProfile]);

    const quotesWithDetails = useMemo(() => {
        return quotes.map(quote => {
            const client = clients.find(c => c.id === quote.clientId);
            const total = calculateTotal(quote.items, hourlyRate);
            return { ...quote, clientName: client?.name || 'Cliente não encontrado', total };
        }).sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
    }, [quotes, clients, hourlyRate, calculateTotal]);

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-white">Orçamentos</h1>
                <GlassButton onClick={onNewQuote} className="!bg-indigo-500/80 hover:!bg-indigo-500"><AddIcon size={16} /><span>Novo Orçamento</span></GlassButton>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {quotesWithDetails.map(quote => (
                    <GlassCard 
                        key={quote.id} 
                        onClick={() => onLoadQuote(quote)}
                        className="group relative cursor-pointer hover:bg-white/20 transition-all duration-200 hover:scale-[1.01] flex flex-col justify-between"
                    >
                        <button
                            onClick={(e) => handleDeleteClick(e, quote)}
                            className="absolute bottom-2 left-2 p-1.5 bg-red-500/20 rounded-full text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/40 transition-all z-20"
                            title="Excluir Orçamento"
                        >
                            <DeleteIcon size={16} />
                        </button>
                        <div className="relative">
                            <div className="flex justify-between items-start">
                                <p className="font-bold text-lg text-white pr-8">{quote.projectName}</p>
                                <StatusPillDropdown quote={quote} onStatusChange={handleStatusChange} />
                            </div>
                            <p className="text-sm text-gray-300 mt-1">{quote.clientName}</p>
                        </div>
                        <div className="mt-4 pt-4 border-t border-white/10 text-right">
                            <p className="text-xs text-gray-400">{quote.quoteNumber}</p>
                            <p className="text-2xl font-bold text-emerald-400">{quote.total.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})}</p>
                        </div>
                    </GlassCard>
                ))}
            </div>
        </div>
    );
}

// --- INÍCIO DA ALTERAÇÃO: Recebendo e utilizando as novas props ---
const QuoteGeneratorPage = ({ onDeleteQuote, quoteIdToOpen, setQuoteIdToOpen }) => {
    const { quotes } = useData();
    const [view, setView] = useState('dashboard');
    const [quoteToEdit, setQuoteToEdit] = useState(null);

    const handleNewQuote = () => {
        setQuoteToEdit(null);
        setView('editor');
    };

    const handleLoadQuote = (quote) => {
        setQuoteToEdit(quote);
        setView('editor');
    };

    useEffect(() => {
        if (quoteIdToOpen) {
            const quoteToOpen = quotes.find(q => q.id === quoteIdToOpen);
            if (quoteToOpen) {
                handleLoadQuote(quoteToOpen);
            }
            setQuoteIdToOpen(null); // Reseta o gatilho
        }
    }, [quoteIdToOpen, quotes, setQuoteIdToOpen]);
    // --- FIM DA ALTERAÇÃO ---

    if (view === 'editor') {
        return (
            <div className="p-4">
                <QuoteEditorView initialQuote={quoteToEdit} onBack={() => setView('dashboard')} />
            </div>
        );
    }

    return <QuotesDashboardView onNewQuote={handleNewQuote} onLoadQuote={handleLoadQuote} onDeleteQuote={onDeleteQuote} />;
};

export default QuoteGeneratorPage;